import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    User,
    UserCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './config';

export interface UserData {
    uid: string;
    email: string;
    displayName: string;
    role: 'admin' | 'security' | 'client';
    createdAt: Date;
}

export interface CompanyData {
    id: string;
    name?: string;
    companyName?: string;
    email?: string;
    emails?: string[];
    logo?: string;
    logoUrl?: string;
    companyLogo?: string;
    companyImg?: string;
    [key: string]: any;
}

export const signUp = async (
    email: string,
    password: string,
    displayName: string,
    role: 'admin' | 'security' | 'client' = 'client'
): Promise<UserCredential> => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Update the user's display name
        await updateProfile(user, {
            displayName: displayName
        });

        // Save additional user data to Firestore
        const userData: UserData = {
            uid: user.uid,
            email: user.email!,
            displayName: displayName,
            role: role,
            createdAt: new Date()
        };

        await setDoc(doc(db, 'users', user.uid), userData);

        return userCredential;
    } catch (error) {
        throw error;
    }
};

export const signIn = async (email: string, password: string): Promise<UserCredential> => {
    try {
        const normalizedEmail = email.toLowerCase().trim();
        
        // First, try to sign in with Firebase Auth (this is fast)
        // We'll verify access after successful authentication
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // After successful sign-in, verify the user has access to a company
        // This check happens after auth, so valid credentials get fast response
        try {
            const companiesRef = collection(db, 'companies');
            
            // Check if email is in companies collection (admin/company owners)
            const q1 = query(companiesRef, where('email', '==', normalizedEmail));
            const querySnapshot1 = await getDocs(q1);
            
            if (!querySnapshot1.empty) {
                return userCredential; // User is a company owner/admin
            }
            
            // Try checking for emails array field in companies
            const q2 = query(companiesRef, where('emails', 'array-contains', normalizedEmail));
            const querySnapshot2 = await getDocs(q2);
            
            if (!querySnapshot2.empty) {
                return userCredential; // User email is in company's emails array
            }
            
            // Check if email exists in any staff subcollection using collection group query
            // Add timeout to prevent hanging if index doesn't exist
            const checkStaffAccess = async () => {
                try {
                    const staffCollectionGroup = collectionGroup(db, 'staff');
                    const staffQuery = query(staffCollectionGroup, where('email', '==', normalizedEmail));
                    const staffSnapshot = await getDocs(staffQuery);
                    return !staffSnapshot.empty;
                } catch (err: any) {
                    console.warn('Collection group query failed, trying alternative method:', err);
                    // Fallback 1: check if user exists in users collection (created during staff creation)
                    try {
                        const userDocRef = doc(db, 'users', userCredential.user.uid);
                        const userDoc = await getDoc(userDocRef);
                        if (userDoc.exists()) {
                            return true;
                        }
                    } catch (userErr) {
                        console.warn('Users collection check failed:', userErr);
                    }
                    
                    // Fallback 2: search through all companies manually
                    // This is slower but works without collection group indexes
                    try {
                        const companiesRef = collection(db, 'companies');
                        const allCompanies = await getDocs(companiesRef);
                        
                        for (const companyDoc of allCompanies.docs) {
                            try {
                                const staffDocRef = doc(db, 'companies', companyDoc.id, 'staff', userCredential.user.uid);
                                const staffDoc = await getDoc(staffDocRef);
                                if (staffDoc.exists()) {
                                    const staffData = staffDoc.data();
                                    // Verify email matches
                                    if (staffData.email?.toLowerCase() === normalizedEmail) {
                                        return true;
                                    }
                                }
                            } catch (docErr) {
                                // Continue to next company if this one fails
                                continue;
                            }
                        }
                    } catch (fallbackErr) {
                        console.error('Error in fallback staff search:', fallbackErr);
                    }
                    
                    return false;
                }
            };
            
            const hasStaffAccess = await Promise.race([
                checkStaffAccess(),
                new Promise<boolean>((resolve) => 
                    setTimeout(() => resolve(false), 5000) // 5 second timeout
                )
            ]);
            
            if (hasStaffAccess) {
                return userCredential; // User is staff member
            }
            
            // User signed in successfully but doesn't have company/staff access
            // Sign them out and show error
            await signOut(auth);
            throw new Error('This email is not associated with any company. Please contact your administrator.');
            
        } catch (accessError: any) {
            // If access check fails, sign out the user
            await signOut(auth);
            
            // If it's our custom error, throw it as is
            if (accessError.message && accessError.message.includes('not associated with any company')) {
                throw accessError;
            }
            // Otherwise, throw a generic access error
            throw new Error('This email is not associated with any company. Please contact your administrator.');
        }
        
    } catch (error: any) {
        // If it's already our custom error, throw it as is
        if (error.message && error.message.includes('not associated with any company')) {
            throw error;
        }
        // Otherwise, throw the Firebase auth error (invalid credentials, etc.)
        throw error;
    }
};

export const logout = async (): Promise<void> => {
    try {
        await signOut(auth);
    } catch (error) {
        throw error;
    }
};

export const getCurrentUser = (): User | null => {
    return auth.currentUser;
};

export interface StaffData {
    uid: string;
    email: string;
    displayName: string;
    phone?: string;
    department?: string;
    departments?: string[];
    role: 'staff' | 'supervisor' | 'manager' | 'owner';
    permissions?: {
        staff?: 'view' | 'add' | 'edit' | 'delete' | null;
        dashboard?: 'view' | 'edit' | null;
        [key: string]: 'view' | 'add' | 'edit' | 'delete' | null | undefined;
    };
    access?: {
        staff?: boolean;
        dashboard?: boolean;
        [key: string]: boolean | undefined;
    };
    createdAt: Date;
    createdBy: string;
    isActive?: boolean;
    inactiveReason?: string;
}

export const createStaff = async (
    email: string,
    password: string,
    displayName: string,
    companyId: string,
    phone?: string,
    department?: string,
    departments?: string[],
    role: 'staff' | 'supervisor' | 'manager' | 'owner' = 'staff',
    permissions?: {
        staff?: 'view' | 'add' | 'edit' | 'delete' | null;
        dashboard?: 'view' | 'edit' | null;
        [key: string]: 'view' | 'add' | 'edit' | 'delete' | null | undefined;
    },
    access?: {
        staff?: boolean;
        dashboard?: boolean;
        [key: string]: boolean | undefined;
    },
    isActive: boolean = true,
    inactiveReason?: string,
    adminEmail?: string,
    adminPassword?: string
): Promise<StaffData> => {
    try {
        // Store current user to restore session after creating staff
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('No authenticated user found');
        }

        // Get the current user's ID token before creating staff
        // This will be used to verify the admin session later
        const currentUserToken = await currentUser.getIdToken();
        const currentUserEmail = currentUser.email;
        if (!currentUserEmail) {
            throw new Error('Current user email not found');
        }

        // Create the new staff user
        // Note: This will automatically sign in the new user, logging out the current admin
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const newUser = userCredential.user;

        // Update the new user's display name
        await updateProfile(newUser, {
            displayName: displayName
        });

        // Create staff data
        // Note: Firestore doesn't allow undefined values, so we conditionally include fields
        const staffData: any = {
            uid: newUser.uid,
            email: newUser.email!,
            displayName: displayName,
            role: role,
            permissions: permissions || {},
            access: access || {},
            isActive: isActive,
            createdAt: new Date(),
            createdBy: currentUser.uid
        };

        // Only include optional fields if they have values (to avoid undefined in Firestore)
        if (phone) staffData.phone = phone;
        if (department) staffData.department = department;
        if (departments && departments.length > 0) {
            staffData.departments = departments;
        } else if (department) {
            staffData.departments = [department];
        }
        if (inactiveReason) staffData.inactiveReason = inactiveReason;

        // Save staff data to Firestore at companies/{companyId}/staff/{staffId}
        console.log('Saving staff to Firestore at:', `companies/${companyId}/staff/${newUser.uid}`);
        await setDoc(doc(db, 'companies', companyId, 'staff', newUser.uid), staffData);
        console.log('Staff saved successfully to Firestore');

        // Also save to users collection for compatibility
        await setDoc(doc(db, 'users', newUser.uid), {
            uid: newUser.uid,
            email: newUser.email!,
            displayName: displayName,
            role: role,
            createdAt: new Date()
        });

        // CRITICAL: Sign out the newly created user immediately
        // Firebase automatically signs in newly created users, which logs out the admin
        await signOut(auth);
        
        // IMMEDIATELY sign the admin back in to prevent logout
        // This must happen right after signOut to restore admin session
        if (!adminEmail || !adminPassword) {
            throw new Error('Admin credentials not found. Please log out and log back in, then try creating staff again.');
        }

        try {
            console.log('Restoring admin session immediately...');
            console.log('Admin email:', adminEmail);
            
            // Sign admin back in immediately - this is critical to prevent logout
            await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
            
            // Wait longer for auth state to fully stabilize and AuthContext to process
            // This is important because AuthContext's onAuthStateChanged listener needs time
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Verify the admin is signed in - check multiple times to ensure stability
            let currentUser = auth.currentUser;
            let attempts = 0;
            const maxAttempts = 3;
            
            while (attempts < maxAttempts) {
                currentUser = auth.currentUser;
                if (currentUser?.email?.toLowerCase() === adminEmail.toLowerCase()) {
                    console.log(`✅ Admin session restored successfully! (Attempt ${attempts + 1}) Admin is logged in as`, currentUser.email);
                    break;
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        console.warn(`⚠️ Admin session not stable yet (Attempt ${attempts}). Current user:`, currentUser?.email, '- Retrying...');
                        // Try signing in again
                        await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
                        await new Promise(resolve => setTimeout(resolve, 300));
                    } else {
                        throw new Error(`Failed to restore admin session after ${maxAttempts} attempts. Current user: ${currentUser?.email}`);
                    }
                }
            }
            
            // Final verification
            if (auth.currentUser?.email?.toLowerCase() !== adminEmail.toLowerCase()) {
                throw new Error('Admin session verification failed. Please sign in again.');
            }
        } catch (signInError: any) {
            console.error('❌ CRITICAL ERROR: Failed to restore admin session:', signInError);
            // Staff was created successfully, but admin session couldn't be restored
            throw new Error(`Staff created successfully, but failed to restore your session. Error: ${signInError.message}. Please sign in again.`);
        }
        
        return staffData;
    } catch (error) {
        console.error('Error creating staff:', error);
        throw error;
    }
};

export const getCompanyByEmail = async (email: string): Promise<CompanyData | null> => {
    try {
        const companiesRef = collection(db, 'companies');
        const normalizedEmail = email.toLowerCase().trim();
        
        console.log('Searching for company with email:', normalizedEmail);
        
        // First, try to find by direct email field
        const q1 = query(companiesRef, where('email', '==', normalizedEmail));
        const querySnapshot1 = await getDocs(q1);
        
        console.log('Query 1 (email field) results:', querySnapshot1.size, 'documents');
        
        if (!querySnapshot1.empty) {
            const doc = querySnapshot1.docs[0];
            const data = doc.data();
            console.log('Found company by email field:', data);
            // Normalize the data to ensure we have name and logo
            const normalizedData: CompanyData = {
                id: doc.id,
                name: data.name || data.companyName || 'Company',
                companyName: data.companyName || data.name,
                logo: data.logo || data.logoUrl || data.companyLogo || data.companyImg,
                companyImg: data.companyImg || data.logo || data.logoUrl || data.companyLogo,
                email: data.email,
                emails: data.emails,
                ...data
            };
            return normalizedData;
        }
        
        // If not found, try to find by emails array
        const q2 = query(companiesRef, where('emails', 'array-contains', normalizedEmail));
        const querySnapshot2 = await getDocs(q2);
        
        console.log('Query 2 (emails array) results:', querySnapshot2.size, 'documents');
        
        if (!querySnapshot2.empty) {
            const doc = querySnapshot2.docs[0];
            const data = doc.data();
            console.log('Found company by emails array:', data);
            // Normalize the data to ensure we have name and logo
            const normalizedData: CompanyData = {
                id: doc.id,
                name: data.name || data.companyName || 'Company',
                companyName: data.companyName || data.name,
                logo: data.logo || data.logoUrl || data.companyLogo || data.companyImg,
                companyImg: data.companyImg || data.logo || data.logoUrl || data.companyLogo,
                email: data.email,
                emails: data.emails,
                ...data
            };
            return normalizedData;
        }
        
        console.log('No company found for email:', normalizedEmail);
        return null;
    } catch (error) {
        console.error('Error fetching company:', error);
        return null;
    }
};







